// Voice messages on screen: the player in a message (play/pause, a
// waveform to scrub, 1×/1.5×/2×) and the composer's recorder (hold the mic
// to record and release to send, or tap it to keep recording; slide left
// or press Escape to cancel).
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { session } from "../api";
import type { Message } from "../chat/chat";
import { urlExpired, type Media } from "../chat/media";
import { VoiceRecording, type RecordedClip } from "../chat/recorder";
import { uploadErrorText, uploadVoice } from "../chat/upload";
import {
  canPlayClip,
  clipDuration,
  clockText,
  dragOutcome,
  keySeek,
  liveBars,
  microphoneErrorText,
  nextRate,
  playedBars,
  rateText,
  releaseOutcome,
  seekFraction,
  voiceAttachment,
  voiceLength,
  voiceText,
  waveformHeights,
  type PlaybackRate,
  type VoiceClip,
} from "../chat/voice";

// ------------------------------------------------------------------ player

/** The clip playing now: starting another pauses it. */
let playingNow: HTMLAudioElement | null = null;

function Bars(props: { heights: readonly number[]; played: number; className?: string }) {
  const { heights, played } = props;
  const width = heights.length * 3;
  return (
    <svg
      className={props.className ?? "chat-voice-bars"}
      viewBox={`0 0 ${width} 24`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {heights.map((h, i) => {
        const height = Math.max(1.5, h * 24);
        return (
          <rect
            key={i}
            x={i * 3 + 0.5}
            y={(24 - height) / 2}
            width={2}
            height={height}
            rx={1}
            className={i < played ? "played" : undefined}
          />
        );
      })}
    </svg>
  );
}

export function VoicePlayer(props: {
  media: Media;
  clip: VoiceClip | null | undefined;
  onExpired?: (() => void) | undefined;
}) {
  const { media, clip, onExpired } = props;
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [rate, setRate] = useState<PlaybackRate>(1);
  const [failed, setFailed] = useState(false);
  const [playable] = useState(() =>
    canPlayClip(media.mime, (type) => document.createElement("audio").canPlayType(type)),
  );
  const durationMs = clip?.duration_ms ?? media.duration_ms ?? 0;
  const scrubbing = useRef<number | null>(null);

  // Follow playback smoothly (timeupdate comes only a few times a second).
  useEffect(() => {
    if (!playing) return;
    let frame = requestAnimationFrame(function tick() {
      const el = audio.current;
      if (el && scrubbing.current === null) setPositionMs(Math.min(durationMs, el.currentTime * 1000));
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [playing, durationMs]);

  useEffect(() => {
    if (audio.current) audio.current.playbackRate = rate;
  }, [rate]);

  useEffect(
    () => () => {
      if (playingNow === audio.current) playingNow = null;
    },
    [],
  );

  function toggle() {
    const el = audio.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    setFailed(false);
    el.playbackRate = rate;
    if (Math.abs(el.currentTime * 1000 - positionMs) > 250) el.currentTime = positionMs / 1000;
    el.play().catch(() => undefined);
  }

  function seek(ms: number) {
    setPositionMs(ms);
    const el = audio.current;
    if (el) el.currentTime = ms / 1000;
  }

  function seekAt(event: PointerEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    seek(seekFraction(event.clientX, rect.left, rect.width) * durationMs);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const target = keySeek(event.key, positionMs, durationMs);
    if (target === null) return;
    event.preventDefault();
    seek(target);
  }

  const heights = waveformHeights(clip?.waveform);
  const fraction = durationMs > 0 ? positionMs / durationMs : 0;
  const shown = playing || positionMs > 0 ? clockText(positionMs) : voiceLength(durationMs);

  if (!playable) {
    return (
      <div className="chat-voice">
        <Bars heights={heights} played={0} />
        <span className="chat-voice-time">{voiceLength(durationMs)}</span>
        <span className="chat-voice-note">
          This browser can't play this voice message.{" "}
          <a href={media.url} download>
            Download
          </a>
        </span>
      </div>
    );
  }

  return (
    <div className="chat-voice">
      <button
        type="button"
        className="chat-voice-play"
        aria-label={playing ? "Pause voice message" : "Play voice message"}
        onClick={toggle}
      >
        {playing ? "❚❚" : "▶"}
      </button>
      <div
        className="chat-voice-scrub"
        role="slider"
        tabIndex={0}
        aria-label="Position"
        aria-valuemin={0}
        aria-valuemax={Math.round(durationMs / 1000)}
        aria-valuenow={Math.round(positionMs / 1000)}
        aria-valuetext={`${clockText(positionMs)} of ${voiceLength(durationMs)}`}
        onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          scrubbing.current = event.pointerId;
          seekAt(event);
        }}
        onPointerMove={(event) => {
          if (scrubbing.current === event.pointerId) seekAt(event);
        }}
        onPointerUp={() => (scrubbing.current = null)}
        onPointerCancel={() => (scrubbing.current = null)}
      >
        <Bars heights={heights} played={playedBars(fraction, heights.length)} />
      </div>
      <span className="chat-voice-time">{shown}</span>
      <button
        type="button"
        className="chat-voice-rate"
        aria-label={`Playback speed ${rateText(rate)}`}
        onClick={() => setRate(nextRate(rate))}
      >
        {rateText(rate)}
      </button>
      {failed && (
        <span className="chat-voice-note error" role="alert">
          Couldn't play it
        </span>
      )}
      <audio
        ref={audio}
        src={media.url}
        preload="none"
        onPlay={(event) => {
          if (playingNow && playingNow !== event.currentTarget) playingNow.pause();
          playingNow = event.currentTarget;
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={(event) => {
          setPlaying(false);
          setPositionMs(0);
          event.currentTarget.currentTime = 0;
        }}
        onError={() => {
          setPlaying(false);
          // A pending clip has no message to fetch again yet.
          if (urlExpired(media.url, Date.now()) && onExpired) onExpired();
          else setFailed(true);
        }}
      />
    </div>
  );
}

/** A voice message's player (its clip is the one audio attachment). */
export function MessageVoice(props: { message: Message; onExpired?: (() => void) | undefined }) {
  const media = voiceAttachment(props.message);
  if (!media) return <span className="chat-text">{voiceText(props.message)}</span>;
  return <VoicePlayer media={media} clip={props.message.voice} onExpired={props.onExpired} />;
}

// ---------------------------------------------------------------- recorder

export interface VoiceSetup {
  /** What `MediaRecorder` records here. */
  type: string;
  maxMs: number;
  maxBytes: number;
}

type RecorderState =
  | { phase: "idle"; notice: string | null }
  | { phase: "recording"; holding: boolean; started: boolean; bars: number[]; elapsedMs: number }
  | { phase: "uploading"; progress: number }
  | { phase: "failed"; error: string };

const IDLE: RecorderState = { phase: "idle", notice: null };

/**
 * The composer's voice recorder for one conversation: records, uploads the
 * clip and hands it to `send` (which posts the message); changing chats or
 * leaving the page throws away whatever is under way.
 */
export function useVoiceRecorder(
  conversationId: string,
  setup: VoiceSetup | null,
  send: (media: Media, clip: VoiceClip) => void,
) {
  const [state, setState] = useState<RecorderState>(IDLE);
  const recording = useRef<VoiceRecording | null>(null);
  const upload = useRef<AbortController | null>(null);
  const clip = useRef<{ recorded: RecordedClip; durationMs: number } | null>(null);
  const sendRef = useRef(send);
  useLayoutEffect(() => {
    sendRef.current = send;
  });
  const finishing = useRef(false);

  const reset = useCallback((next: RecorderState = IDLE) => {
    recording.current?.cancel();
    recording.current = null;
    upload.current?.abort();
    upload.current = null;
    clip.current = null;
    finishing.current = false;
    setState(next);
  }, []);

  useEffect(() => () => reset(), [conversationId, reset]);

  const uploadClip = useCallback(() => {
    const current = clip.current;
    if (!current) return;
    const controller = new AbortController();
    upload.current = controller;
    setState({ phase: "uploading", progress: 0 });
    uploadVoice(
      session,
      conversationId,
      { blob: current.recorded.blob, contentType: current.recorded.contentType, durationMs: current.durationMs },
      (progress) => {
        if (!controller.signal.aborted) setState({ phase: "uploading", progress });
      },
      controller.signal,
    ).then(
      (media) => {
        if (controller.signal.aborted) return;
        upload.current = null;
        clip.current = null;
        setState(IDLE);
        sendRef.current(media, { duration_ms: current.durationMs, waveform: current.recorded.waveform });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        upload.current = null;
        setState({ phase: "failed", error: uploadErrorText(error) });
      },
    );
  }, [conversationId]);

  /** Stops recording and sends the clip. */
  const finish = useCallback(() => {
    const current = recording.current;
    if (!current || !setup || finishing.current) return;
    finishing.current = true;
    current.stop().then(
      (recorded) => {
        if (recording.current !== current) return;
        recording.current = null;
        finishing.current = false;
        if (recorded.blob.size > setup.maxBytes) {
          setState({ phase: "idle", notice: "That voice message is too large to send" });
          return;
        }
        clip.current = { recorded, durationMs: clipDuration(recorded.elapsedMs, setup.maxMs) };
        uploadClip();
      },
      (error: unknown) => {
        if (recording.current !== current) return;
        reset({ phase: "idle", notice: microphoneErrorText(error) });
      },
    );
  }, [setup, uploadClip, reset]);

  const finishRef = useRef(finish);
  useLayoutEffect(() => {
    finishRef.current = finish;
  });

  const start = useCallback(
    (holding: boolean) => {
      if (!setup || recording.current) return;
      const current = new VoiceRecording(
        setup.type,
        (levels, elapsedMs) => {
          if (recording.current !== current || finishing.current) return;
          setState((state) =>
            state.phase === "recording" ? { ...state, started: true, bars: liveBars(levels), elapsedMs } : state,
          );
          // At the server's limit the clip goes as it is.
          if (elapsedMs >= setup.maxMs) finishRef.current();
        },
        (error) => {
          if (recording.current === current) reset({ phase: "idle", notice: microphoneErrorText(error) });
        },
      );
      recording.current = current;
      setState({ phase: "recording", holding, started: false, bars: liveBars([]), elapsedMs: 0 });
      current.start().then(
        () => {
          if (recording.current === current)
            setState((state) => (state.phase === "recording" ? { ...state, started: true } : state));
        },
        (error: unknown) => {
          if (recording.current === current) reset({ phase: "idle", notice: microphoneErrorText(error) });
        },
      );
    },
    [setup, reset],
  );

  const lock = useCallback(
    () => setState((state) => (state.phase === "recording" ? { ...state, holding: false } : state)),
    [],
  );

  /** The held mic button was let go after `heldMs`. */
  const release = useCallback(
    (heldMs: number) => {
      const current = recording.current;
      if (!current) return;
      const outcome = releaseOutcome(heldMs, current.elapsed());
      if (outcome === "lock") lock();
      else if (outcome === "send") finish();
      else reset({ phase: "idle", notice: "Hold to record, or tap the microphone to keep recording" });
    },
    [lock, finish, reset],
  );

  // Escape cancels a recording.
  const recordingNow = state.phase === "recording";
  useEffect(() => {
    if (!recordingNow) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        reset();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [recordingNow, reset]);

  return {
    state,
    start,
    lock,
    release,
    finish,
    retry: uploadClip,
    cancel: () => reset(),
    dismiss: () => setState(IDLE),
  };
}

export type VoiceRecorder = ReturnType<typeof useVoiceRecorder>;

/**
 * The mic button: hold to record (release sends, slide left cancels, slide
 * up locks) or tap / press Enter to keep recording; while it keeps
 * recording, the same button sends.
 */
export function MicButton(props: { recorder: VoiceRecorder; disabled?: boolean }) {
  const { recorder } = props;
  const { state } = recorder;
  const press = useRef<{ id: number; x: number; y: number; at: number } | null>(null);
  /** The click that follows a pointer press is part of it (Infinity while pressed). */
  const ignoreClickUntil = useRef(0);
  const locked = state.phase === "recording" && !state.holding;
  const holding = state.phase === "recording" && state.holding;

  return (
    <button
      type="button"
      className={`chat-mic${holding ? " holding" : ""}${locked ? " send" : ""}`}
      aria-label={locked ? "Send voice message" : "Record a voice message"}
      title={locked ? "Send" : "Hold to record, or tap to keep recording"}
      disabled={props.disabled || (state.phase !== "idle" && state.phase !== "recording")}
      onPointerDown={(event) => {
        if (event.button !== 0 || state.phase !== "idle") return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        press.current = { id: event.pointerId, x: event.clientX, y: event.clientY, at: performance.now() };
        ignoreClickUntil.current = Infinity;
        recorder.start(true);
      }}
      onPointerMove={(event) => {
        const p = press.current;
        if (!p || p.id !== event.pointerId) return;
        const outcome = dragOutcome(event.clientX - p.x, event.clientY - p.y);
        if (outcome === null) return;
        press.current = null;
        if (outcome === "cancel") recorder.cancel();
        else recorder.lock();
      }}
      onPointerUp={(event) => {
        const p = press.current;
        if (ignoreClickUntil.current === Infinity) ignoreClickUntil.current = performance.now() + 500;
        if (!p || p.id !== event.pointerId) return;
        press.current = null;
        recorder.release(performance.now() - p.at);
      }}
      onPointerCancel={(event) => {
        if (ignoreClickUntil.current === Infinity) ignoreClickUntil.current = 0;
        if (press.current?.id !== event.pointerId) return;
        press.current = null;
        // The browser took the pointer (a scroll, say): keep the recording, locked.
        recorder.lock();
      }}
      onClick={() => {
        if (performance.now() < ignoreClickUntil.current) return;
        if (state.phase === "idle") recorder.start(false);
        else if (locked) recorder.finish();
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {locked ? "Send" : "🎤"}
    </button>
  );
}

/** What the composer shows instead of the text box while a clip is recorded, uploaded or failed. */
export function VoiceBar(props: { recorder: VoiceRecorder }) {
  const { recorder } = props;
  const { state } = recorder;
  if (state.phase === "recording") {
    return (
      <div className="chat-voice-recording" role="status" aria-label="Recording a voice message">
        <button type="button" className="chat-icon-button" aria-label="Cancel recording" onClick={recorder.cancel}>
          🗑
        </button>
        <span className={`chat-voice-dot${state.started ? " live" : ""}`} aria-hidden="true" />
        <span className="chat-voice-time">{state.started ? clockText(state.elapsedMs) : "Starting…"}</span>
        <Bars heights={state.bars} played={state.bars.length} className="chat-voice-bars live" />
        <span className="chat-voice-hint muted">
          {state.holding ? "◀ Slide to cancel · ▲ to lock" : "Esc to cancel"}
        </span>
      </div>
    );
  }
  if (state.phase === "uploading") {
    return (
      <div className="chat-voice-recording" role="status">
        <button type="button" className="chat-icon-button" aria-label="Cancel" onClick={recorder.cancel}>
          ×
        </button>
        <span className="chat-voice-hint">Sending voice message…</span>
        <progress max={1} value={state.progress} aria-label="Upload progress" />
      </div>
    );
  }
  if (state.phase === "failed") {
    return (
      <div className="chat-voice-recording" role="alert">
        <span className="chat-voice-hint error">Voice message not sent: {state.error}</span>
        <button type="button" className="link-button" onClick={recorder.retry}>
          Retry
        </button>
        <button type="button" className="link-button" onClick={recorder.cancel}>
          Discard
        </button>
      </div>
    );
  }
  return null;
}

/** Why the last recording didn't start or go, above the composer. */
export function VoiceNotice(props: { recorder: VoiceRecorder }) {
  const { state } = props.recorder;
  if (state.phase !== "idle" || !state.notice) return null;
  return (
    <p className="chat-tray-notice error" role="alert">
      {state.notice}{" "}
      <button type="button" className="link-button" onClick={props.recorder.dismiss}>
        Dismiss
      </button>
    </p>
  );
}
